/**
 * Organizations API
 *
 * Handles organization management and member operations.
 */

import { api } from './client'

/**
 * Create a new organization
 *
 * @param {Object} params
 * @param {string} params.name - Organization name
 * @param {string} [params.slug] - URL-friendly slug (auto-generated if not provided)
 * @param {string} [params.description] - Organization description
 * @param {string} [params.website] - Website URL
 * @param {string} [params.logoUrl] - Logo URL
 * @param {string} [params.subscriptionPlanCode='standard'] - Subscription plan
 * @param {Object} [params.settings] - Organization settings (contactEmail, contactPhone, etc.)
 * @returns {Promise<Object>} Created organization
 */
export async function createOrganization({
  name,
  slug = null,
  description = null,
  website = null,
  logoUrl = null,
  subscriptionPlanCode = 'standard',
  settings = null,
}) {
  const body = {
    name,
    slug,
    description,
    website,
    logo_url: logoUrl,
    subscription_plan_code: subscriptionPlanCode,
  }

  if (settings) {
    body.settings = settings
  }

  return api.post('/organizations', body)
}

/**
 * List organizations the current user belongs to
 *
 * @returns {Promise<Array>} List of organizations with user's role
 */
export async function listOrganizations() {
  return api.get('/organizations')
}

/**
 * Get organization details
 *
 * @param {string} orgId - Organization UUID
 * @returns {Promise<Object>} Organization details
 */
export async function getOrganization(orgId) {
  return api.get(`/organizations/${orgId}`)
}

/**
 * Update organization details
 *
 * @param {string} orgId - Organization UUID
 * @param {Object} updates
 * @param {string} [updates.name] - New name
 * @param {string} [updates.description] - New description
 * @param {string} [updates.website] - New website
 * @param {string} [updates.logoUrl] - New logo URL
 * @param {Object} [updates.settings] - New settings
 * @returns {Promise<Object>} Updated organization
 */
export async function updateOrganization(orgId, { name, description, website, logoUrl, settings }) {
  const body = {}
  if (name !== undefined) body.name = name
  if (description !== undefined) body.description = description
  if (website !== undefined) body.website = website
  if (logoUrl !== undefined) body.logo_url = logoUrl
  if (settings !== undefined) body.settings = settings

  return api.patch(`/organizations/${orgId}`, body)
}

/**
 * Delete an organization
 *
 * @param {string} orgId - Organization UUID
 * @returns {Promise<Object>} Success message
 */
export async function deleteOrganization(orgId) {
  return api.delete(`/organizations/${orgId}`)
}

// ==========================================
// Member Management
// ==========================================

/**
 * List members of an organization
 *
 * @param {string} orgId - Organization UUID
 * @returns {Promise<Array>} List of members
 */
export async function getMembers(orgId) {
  return api.get(`/organizations/${orgId}/members`)
}

/**
 * Invite a user to the organization
 *
 * @param {string} orgId - Organization UUID
 * @param {string} email - Email of user to invite
 * @param {string} [roleCode='viewer'] - Role to assign
 * @returns {Promise<Object>} Created membership
 */
export async function inviteMember(orgId, email, roleCode = 'viewer') {
  return api.post(`/organizations/${orgId}/members`, {
    email,
    role_code: roleCode,
  })
}

/**
 * Update a member's role
 *
 * @param {string} orgId - Organization UUID
 * @param {string} userId - User UUID
 * @param {string} roleCode - New role code
 * @returns {Promise<Object>} Updated membership
 */
export async function updateMemberRole(orgId, userId, roleCode) {
  return api.patch(`/organizations/${orgId}/members/${userId}`, {
    role_code: roleCode,
  })
}

/**
 * Remove a member from the organization
 *
 * @param {string} orgId - Organization UUID
 * @param {string} userId - User UUID
 * @returns {Promise<Object>} Success message
 */
export async function removeMember(orgId, userId) {
  return api.delete(`/organizations/${orgId}/members/${userId}`)
}

// ==========================================
// Invitation Management
// ==========================================

/**
 * Get pending invitations for an organization
 *
 * @param {string} orgId - Organization UUID
 * @returns {Promise<Array>} List of pending invitations
 */
export async function getInvitations(orgId) {
  return api.get(`/organizations/${orgId}/invitations`)
}

/**
 * Revoke a pending invitation
 *
 * @param {string} orgId - Organization UUID
 * @param {string} invitationId - Invitation UUID
 * @returns {Promise<Object>} Success message
 */
export async function revokeInvitation(orgId, invitationId) {
  return api.delete(`/organizations/${orgId}/invitations/${invitationId}`)
}

/**
 * Verify an invitation token (PUBLIC - no auth required)
 *
 * Used during signup to validate invite link and show org context.
 *
 * @param {string} token - Invitation token from the invite link
 * @returns {Promise<Object>} Invitation details {email, role_code, organization_name, organization_id, expires_at}
 * @throws {Error} 404 if token not found, 410 if token already used or expired
 */
export async function verifyInvitationToken(token) {
  return api.get(`/organizations/invitations/verify/${token}`)
}

// ==========================================
// Plan-related Helpers
// ==========================================

/**
 * Get default plan limits
 *
 * Plan pricing (INR):
 * - Standard: ₹250/month
 * - Business: ₹1000/month
 * - Enterprise: Custom pricing
 *
 * @param {string} planCode - Plan code
 * @returns {Object} Plan limits
 */
export function getPlanLimits(planCode) {
  const limits = {
    standard: {
      max_surveys: 10,
      max_responses_per_survey: 1000,
      max_members: 5,
      price_monthly: 250,
      currency: 'INR',
      features: ['basic_analytics', 'advanced_analytics', 'csv_export'],
    },
    business: {
      max_surveys: 50,
      max_responses_per_survey: 10000,
      max_members: 20,
      price_monthly: 1000,
      currency: 'INR',
      features: ['basic_analytics', 'advanced_analytics', 'csv_export', 'custom_branding', 'api'],
    },
    enterprise: {
      max_surveys: -1, // unlimited
      max_responses_per_survey: -1,
      max_members: -1,
      price_monthly: null, // custom pricing
      currency: 'INR',
      features: [
        'basic_analytics',
        'advanced_analytics',
        'csv_export',
        'custom_branding',
        'api',
        'sso',
        'audit_logs',
      ],
    },
  }

  return limits[planCode] || limits.standard
}

/**
 * Check if plan has a specific feature
 *
 * @param {string} planCode - Plan code
 * @param {string} feature - Feature name
 * @returns {boolean}
 */
export function hasFeature(planCode, feature) {
  const limits = getPlanLimits(planCode)
  return limits.features.includes(feature)
}
